import React from 'react';
import { useEducationCategories, useEducationModules, useUserProgress } from '@/hooks/useEducation';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { BookOpen, Clock, Award, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export const EducationCatalog: React.FC = () => {
  const { user } = useAuth();
  const { data: categories, isLoading: categoriesLoading } = useEducationCategories();
  const { data: modules, isLoading: modulesLoading } = useEducationModules();
  const { data: userProgress } = useUserProgress(user?.id);

  const getModuleProgress = (moduleId: string) => {
    return userProgress?.find(p => p.module_id === moduleId);
  };

  const getCategoryProgress = (categoryId: string) => {
    const categoryModules = modules?.filter(m => m.category_id === categoryId) || [];
    const completedModules = categoryModules.filter(m => {
      const progress = getModuleProgress(m.id);
      return progress?.status === 'completed';
    });
    
    return {
      completed: completedModules.length,
      total: categoryModules.length,
      percentage: categoryModules.length > 0 ? (completedModules.length / categoryModules.length) * 100 : 0
    };
  };

  const getTotalPoints = (categoryId: string) => {
    const categoryModules = modules?.filter(m => m.category_id === categoryId) || [];
    return categoryModules.reduce((total, module) => {
      const progress = getModuleProgress(module.id);
      return total + (progress?.status === 'completed' ? module.points : 0);
    }, 0);
  };

  if (categoriesLoading || modulesLoading) {
    return (
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader>
              <div className="h-6 bg-muted rounded w-3/4"></div>
              <div className="h-4 bg-muted rounded w-full"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="h-4 bg-muted rounded w-1/2"></div>
                <div className="h-2 bg-muted rounded w-full"></div>
                <div className="h-10 bg-muted rounded w-full"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {categories?.map((category) => {
          const categoryModules = modules?.filter(m => m.category_id === category.id) || [];
          const progress = getCategoryProgress(category.id);
          const totalPoints = getTotalPoints(category.id);
          const totalDuration = categoryModules.reduce((sum, m) => sum + m.duration_minutes, 0);

          return (
            <Card key={category.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">{category.name}</CardTitle>
                  {progress.percentage === 100 && (
                    <Badge variant="default" className="bg-green-500">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Complete
                    </Badge>
                  )}
                </div>
                <CardDescription>{category.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    <span>{categoryModules.length} modules</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{totalDuration}min</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    <span>{totalPoints} pts</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress</span>
                    <span>{progress.completed}/{progress.total} modules</span>
                  </div>
                  <Progress value={progress.percentage} className="h-2" />
                </div>

                <div className="space-y-2">
                  {categoryModules.slice(0, 3).map((module) => {
                    const moduleProgress = getModuleProgress(module.id);
                    return (
                      <div key={module.id} className="flex items-center justify-between text-sm">
                        <span className="flex-1 truncate">{module.title}</span>
                        <Badge 
                          variant={moduleProgress?.status === 'completed' ? 'default' : 'secondary'}
                          className="ml-2"
                        >
                          {moduleProgress?.status === 'completed' ? 'Done' : 
                           moduleProgress?.status === 'in_progress' ? 'In Progress' : 'Start'}
                        </Badge>
                      </div>
                    );
                  })}
                  {categoryModules.length > 3 && (
                    <div className="text-sm text-muted-foreground">
                      +{categoryModules.length - 3} more modules
                    </div>
                  )}
                </div>

                <Button asChild className="w-full">
                  <Link to={`/education/category/${category.id}`}>
                    {progress.percentage > 0 ? 'Continue Learning' : 'Start Course'}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};